import { isValidCanadaPostalCode } from "utils/validator";

import { FieldError } from "../../error";
import { Field } from "../../field";
import { BusinessOwner, StoreLocation } from "../../fieldsets";
import { Merchant } from "../../merchant";
import { exactLength, maxLength, minLength } from "../../validate";

import { determineNonOwnerApplicable, determineOwnerApplicable } from "./utils";

export class PostalCodeField extends Field<string> {
  protected readonly _collection: BusinessOwner | StoreLocation | null;

  constructor(
    model: Merchant,
    collection: BusinessOwner | StoreLocation | null,
    { propertyName }: { propertyName: string }
  ) {
    super(model, {
      dataType: "string",
      propertyName,
      uiFieldType: "Input",
      uiInputBaseProps: {
        inputMode: "numeric",
        autoComplete: "postal-code",
      },
    });

    this._collection = collection;
  }

  get applicable(): boolean {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;
    const businessType = this._model.companyInformation.businessType.value;

    if (this._collection instanceof BusinessOwner) {
      return determineOwnerApplicable(registeredCountry);
    } else if (this._collection instanceof StoreLocation) {
      return true;
    }

    // non collection related (ie company information)
    return determineNonOwnerApplicable(registeredCountry, businessType);
  }

  get testValue(): any {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;

    switch (registeredCountry) {
      case "Canada":
        return "D4F 3D3";
      case "United Kingdom":
        return "123456";
      case "Australia":
      case "New Zealand":
        return "1234";
      case "United States":
        return "123456";
      default:
        return "123456";
    }
  }

  get required(): boolean {
    return this.applicable;
  }

  get shown(): boolean {
    return this.applicable;
  }

  _validate(): void {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;

    const valueToValidate = "" + this.value;

    let errorMsg = "";

    if (registeredCountry === "United States") {
      // https://en.wikipedia.org/wiki/ZIP_Code#ZIP+4
      //  five digits of the ZIP Code, followed by a hyphen and four digits
      if (!minLength(5)(valueToValidate) || !maxLength(10)(valueToValidate)) {
        errorMsg = "Min length 5, max length 10";
      }
    } else if (registeredCountry === "Australia") {
      // https://en.wikipedia.org/wiki/Postcodes_in_Australia
      // Postcodes in Australia have four digits
      if (!exactLength(4)(valueToValidate)) {
        errorMsg = "Exact length 4";
      }
    } else if (registeredCountry === "United Kingdom") {
      //en.wikipedia.org/wiki/Postcodes_in_the_United_Kingdom
      // ranging from five to eight characters (including a space)
      if (!minLength(5)(valueToValidate) || !maxLength(8)(valueToValidate)) {
        errorMsg = "Min length 5, max length 8";
      }
    } else if (registeredCountry === "Canada") {
      //en.wikipedia.org/wiki/Postal_codes_in_Canada
      // A Canadian postal code is a six-character string, space separating the third and fourth characters
      // and the format should be ANA NAN
      if (!minLength(6)(valueToValidate) || !maxLength(7)(valueToValidate)) {
        errorMsg = "Min length 6, max length 7";
      } else if (!isValidCanadaPostalCode(valueToValidate)) {
        errorMsg = "Postal code must be of format A1A 1A1";
      }
    } else if (registeredCountry === "New Zealand") {
      // https://en.wikipedia.org/wiki/Postcodes_in_New_Zealand
      // Postcodes in New Zealand consist of four digits
      if (!exactLength(4)(valueToValidate)) {
        errorMsg = "Exact length 4";
      }
    }

    if (errorMsg) {
      throw new FieldError(this, errorMsg, "InvalidLength");
    }
  }

  _formatOnChange = (initialVal: string): string => {
    const merchant = this._model;

    if (merchant.companyInformation.registeredCountry.value !== "Canada") {
      return initialVal;
    }

    const upperCasedPostalCode = initialVal.toUpperCase();
    const upperCasedPostalCodeLength = initialVal.length;
    if (
      // Set up branching to avoid stickiness when keying in postal code.
      upperCasedPostalCodeLength === 6 &&
      upperCasedPostalCode.substring(3, 4) !== " "
    ) {
      return (
        upperCasedPostalCode.substring(0, 3) +
        " " +
        upperCasedPostalCode.substring(3)
      );
    }
    return upperCasedPostalCode;
  };
}
